import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import type { ReservationWrapper } from "@b2bportal/core-types";
import type { Itinerary as CarItinerary } from "@b2bportal/ground-api";
import type { HomesReservation } from "@b2bportal/homes-api";
import type { HotelItinerarySummary } from "@b2bportal/lodging-api";
import { Product } from "@b2bportal/purchase-api";
import type { ActivityReservation } from "@b2bportal/rbc-activities-api";
import type { PackageItinerarySummary } from "@b2bportal/rbc-bundled-packages-api";
import type { SupportContext } from "@b2bportal/rbc-hts-assist-api";

export type HtsAssistLobbyCategory = "upcoming" | "past" | "cancelled";

export type HtsAssistLobbyTrip =
  | (Omit<ReservationWrapper<PackageItinerarySummary>, "productType"> & {
      productType: typeof Product.Package;
    })
  | (Omit<ReservationWrapper<HomesReservation>, "productType"> & {
      productType: typeof Product.Homes;
    })
  | (Omit<ReservationWrapper<ActivityReservation>, "productType"> & {
      productType: typeof Product.Activities;
    })
  | (Omit<
      ReservationWrapper<BookedFlightItineraryWithDepartureTime>,
      "productType"
    > & { productType: typeof Product.Flight })
  | (Omit<ReservationWrapper<HotelItinerarySummary>, "productType"> & {
      productType: typeof Product.Hotel;
    })
  | (Omit<ReservationWrapper<CarItinerary>, "productType"> & {
      productType: typeof Product.Ground;
    });

export const categorizeHtsAssistLobbyTrips = (
  trips: HtsAssistLobbyTrip[]
): Record<HtsAssistLobbyCategory, HtsAssistLobbyTrip[]> => ({
  upcoming: trips.filter((trip) => !trip.recentFirst && !trip.isCancelled),
  past: trips.filter((trip) => trip.recentFirst && !trip.isCancelled),
  cancelled: trips.filter((trip) => Boolean(trip.isCancelled)),
});

const SUPPORT_PRODUCT_TYPES: Record<
  HtsAssistLobbyTrip["productType"],
  SupportContext["productType"]
> = {
  [Product.Flight]: "flight",
  [Product.Hotel]: "hotel",
  [Product.Ground]: "car",
  [Product.Activities]: "activity",
  [Product.Package]: "package",
  [Product.Homes]: "home",
};

export const getHtsAssistLobbySupportContext = (
  trip: HtsAssistLobbyTrip
): SupportContext => ({
  bookingId:
    trip.productType === Product.Package
      ? trip.reservation.hopperReservationId
      : trip.reservationId,
  productType: SUPPORT_PRODUCT_TYPES[trip.productType],
});
